import { useController } from 'react-hook-form';

import { Avatar, Box, Divider, Stack, Text } from '@mantine/core';

import { Component } from 'models';

import { DatasheetMantineProviderReset } from 'components/datasheet/DatasheetMantineProviderReset';
import { CompatibleWithPlaceholders } from 'components/forms/fields/compatible-with/components/CompatibleWithPlaceholders';
import { CompatibleWithProducts } from 'components/forms/fields/compatible-with/components/CompatibleWithProducts';
import { LocalNotificationService } from 'services/LocalNotificationService';

import { AddCompatibleComponent } from './components/AddCompatibleComponent';
import { AddNonDcideComponentForm, NonDcideComponent } from './components/AddNonDcideComponentForm';

import cx from './CompatibleWithField.module.scss';

type Props = {
    componentId?: Component['id'];
};

const CompatibleWithField = ({ componentId }: Props) => {
    const {
        field: { value: dcideComponents = [], onChange: onChangeDcideComponents },
    } = useController({ name: 'compatibleWith' });

    const {
        field: { value: nonDcideComponents = [], onChange: onChangeNonDcideComponents },
    } = useController({ name: 'compatibleWithPlaceholders' });

    const onAddDcideComponent = (componentId: string) => {
        if (dcideComponents.includes(componentId)) {
            LocalNotificationService.showError({ message: 'This component has already been added.' });
            return;
        }

        onChangeDcideComponents([...dcideComponents, componentId]);
    };

    const onAddNonDcideComponent = (component: NonDcideComponent) => {
        onChangeNonDcideComponents([...nonDcideComponents, component]);
    };

    const showProducts = dcideComponents?.length > 0 || nonDcideComponents?.length > 0;

    return (
        <Box className={cx.wrapper}>
            <Box className={cx.form}>
                <Text fw={600} mb="xs">
                    <Avatar size="sm" className={cx.number} component="span">
                        1
                    </Avatar>
                    Choose a compatible product from our catalog
                </Text>

                <AddCompatibleComponent onAdd={onAddDcideComponent} />

                <Divider label="or" my="md" />

                <Text fw={600} mb="xs" component="span">
                    <Avatar size="sm" className={cx.number}>
                        2
                    </Avatar>
                    Not finding what you are looking for? Suggest a product!
                </Text>

                <Text mb="xs" c="dimmed">
                    Help us grow our catalog by recommending a product that&apos;s missing.
                </Text>

                <AddNonDcideComponentForm onAdd={onAddNonDcideComponent} compatibleWith={componentId} />
            </Box>

            {showProducts && (
                <Stack gap="xs" className={cx.products}>
                    <CompatibleWithProducts />
                    <CompatibleWithPlaceholders />
                </Stack>
            )}
        </Box>
    );
};

const WrappedCompatibleWithField = (props: Props) => {
    return (
        <DatasheetMantineProviderReset>
            <CompatibleWithField {...props} />
        </DatasheetMantineProviderReset>
    );
};

export { WrappedCompatibleWithField as CompatibleWithField };
